import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, UserCheck, StickyNote, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FollowUpPicker } from "./FollowUpPicker";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const Label = ({ icon: Icon, children }) => (
  <p className="font-mono-label text-[10px] text-slate-500 mb-1.5 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-blue-600" />{children}</p>
);

const Notes = ({ lead, onCountChange, rep, isAdmin, me }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/leads/${lead.id}/notes`).then((r) => setNotes(r.data.filter((n) => !!n.rep_uid === !!rep))).catch(() => {});
  }, [lead.id, rep]);

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/notes`, { text, rep: !!rep });
      setNotes((n) => [data, ...n]);
      setText("");
      onCountChange(1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n) => {
    try {
      await api.delete(`/notes/${n.id}`);
      setNotes((all) => all.filter((x) => x.id !== n.id));
      onCountChange(-1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={rep ? "lead-rep-notes" : "lead-notes"}>
      <Label icon={StickyNote}>{rep ? "Rep Notes" : "Notes"}</Label>
      {rep && <p className="text-[11px] text-slate-500 -mt-1 mb-1.5">{isAdmin ? `Only you and ${lead.potential_by || "the rep"} can see these.` : "Only you and Jeff can see these."}</p>}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Called, left voicemail…" rows={2} data-testid={rep ? "lead-rep-note-input" : "lead-note-input"} />
      <Button onClick={add} disabled={busy || !text.trim()} size="sm" className="mt-2 rounded-full bg-blue-700 hover:bg-blue-800" data-testid={rep ? "lead-rep-note-add" : "lead-note-add"}>{rep ? "Add rep note" : "Add note"}</Button>
      <div className="mt-3 space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm" data-testid={`lead-note-${n.id}`}>
            <div className="flex justify-between gap-2">
              <p className="text-slate-800 whitespace-pre-wrap break-words">{n.text}</p>
              {(!rep || isAdmin || n.author === me) && <button onClick={() => remove(n)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`lead-note-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LeadWork = ({ lead, team, onUpdate, isAdmin, userId, userName }) => (
  <LeadWorkBody lead={lead} team={team} onUpdate={onUpdate} isAdmin={isAdmin} me={userName}
    showRep={(["potential", "signed"].includes(lead.bucket) && (isAdmin || lead[`${lead.bucket}_uid`] === userId)) || (isAdmin && lead.rep_notes_count > 0)} />
);

const LeadWorkBody = ({ lead, team, onUpdate, isAdmin, showRep, me }) => (
  <div className="mt-6 space-y-5 rounded-2xl border border-slate-200 p-4" data-testid="lead-work">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label icon={UserCheck}>Assigned to</Label>
        {isAdmin ? (
          <Select value={lead.assigned_to || "none"} onValueChange={(v) => onUpdate(lead, { assigned_to: v === "none" ? null : v })}>
            <SelectTrigger className="rounded-full h-9" data-testid="lead-assign-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {team.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`lead-assign-option-${m.id}`}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <p className="h-9 flex items-center text-sm text-slate-700" data-testid="lead-assigned-readonly">
            {!lead.assigned_to ? "Unassigned" : team.some((m) => m.id === lead.assigned_to) ? "You (by Jeff)" : "Another rep"}
          </p>
        )}
      </div>
      <div>
        <Label icon={CalendarClock}>Follow-up</Label>
        <FollowUpPicker lead={lead} onUpdate={onUpdate} />
      </div>
    </div>
    {!["potential", "signed"].includes(lead.bucket) && <Notes lead={lead} onCountChange={(d) => onUpdate(lead, null, d)} />}
    {showRep && <Notes rep isAdmin={isAdmin} me={me} lead={lead} onCountChange={(d) => onUpdate(lead, null, d, "rep_notes_count")} />}
  </div>
);
